import { useTranslation } from '@/i18n/client';
import {
  Attribute,
  AttributeCollection,
} from '@/modules/product/api/singleProduct';
import {
  useCurrentLanguageCode,
  useCurrentLocale,
} from '@/shared/utils/client';

import {
  EXCLUDED_ATTRIBUTE_SLUGS,
  EXCLUDED_ATTRIBUTE_VALUES,
} from './constants';
import styles from './ProductAttributes.module.scss';
import { renderTechnicalIcon } from './renderTechnicalIcon';
import { ProductAttributesProps } from './types';
import {
  appendUnitToAttributeValues,
  appendUUIDToAttributes,
  getProductAttributes,
  getTechnicalIcons,
  isAttributeIcon,
  isEssentialInformation,
  moveOtherInformationXIndex,
  moveToEndEssentialInformationIndex,
  sortAttributeIconsByName,
  sortAttributesByName,
  sortSpecificationIcons,
} from './utils';

const AttributeValue = ({ attribute }: { attribute: Attribute }) => {
  const { t } = useTranslation(['products', 'common']);
  const values = appendUnitToAttributeValues(t, attribute);

  switch (attribute.slug) {
    case 'essential_informations':
    case 'other_informations_x':
      return <span dangerouslySetInnerHTML={{ __html: values }}></span>;

    default:
      return <span>{values.replace(/<[^>]*>/g, '')}</span>;
  }
};

export const ProductAttributes = ({ product }: ProductAttributesProps) => {
  const { t } = useTranslation(['products', 'common']);
  const locale = useCurrentLocale();
  const languageCode = useCurrentLanguageCode();

  let productAttributes: Attribute[] | AttributeCollection[] =
    getProductAttributes(t, locale, languageCode, product);
  moveToEndEssentialInformationIndex(productAttributes);
  moveOtherInformationXIndex(productAttributes);
  productAttributes = appendUUIDToAttributes(
    productAttributes,
    product.productId,
    product.type
  );
  const technicalIcons: Attribute[] | AttributeCollection[] =
    getTechnicalIcons(product);

  return (
    <div className={styles.productAttributes__container}>
      {product.type === 'simple' ? (
        <>
          <div className={styles.productAttributes__specification}>
            {(productAttributes as Attribute[]).map((attribute, index) => {
              if (!attribute.inSpecification) {
                return null;
              }

              return (
                <div
                  className={styles['productAttributes__specification--item']}
                  key={index}
                >
                  <span>{attribute.name}</span>
                  <AttributeValue attribute={attribute} />
                </div>
              );
            })}
          </div>

          {technicalIcons.length > 0 ? (
            <div className={styles.productAttributes__technicalIcons}>
              <div className={styles.productAttributes__technicalIcons__inner}>
                {(technicalIcons as Attribute[])
                  .sort(sortSpecificationIcons)
                  .map((icon, index) =>
                    renderTechnicalIcon(
                      index,
                      icon,
                      productAttributes as Attribute[],
                      styles['productAttributes__technicalIcons--item']
                    )
                  )}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {(productAttributes as AttributeCollection[]).map(
            (attributeCollection, key) => {
              const icons = (technicalIcons as AttributeCollection[]).find(
                (i) => i.ean === attributeCollection.ean
              );

              return (
                <>
                  <p
                    className={styles['productAttributes__specification--name']}
                  >
                    {attributeCollection.name}
                  </p>
                  <div className={styles.productAttributes__specification}>
                    {attributeCollection.attributes.map((attribute, index) => {
                      if (!attribute.inSpecification) {
                        return null;
                      }

                      return (
                        <div
                          className={
                            styles['productAttributes__specification--item']
                          }
                          key={index}
                        >
                          <span>{attribute.name}</span>
                          <AttributeValue attribute={attribute} />
                        </div>
                      );
                    })}
                  </div>

                  {icons?.attributes && icons?.attributes?.length > 0 ? (
                    <div className={styles.productAttributes__technicalIcons}>
                      <div
                        className={
                          styles.productAttributes__technicalIcons__inner
                        }
                      >
                        {icons.attributes
                          .sort(sortSpecificationIcons)
                          .map((icon, index) =>
                            renderTechnicalIcon(
                              index,
                              icon,
                              attributeCollection.attributes,
                              styles['productAttributes__technicalIcons--item']
                            )
                          )}
                      </div>
                    </div>
                  ) : null}
                </>
              );
            }
          )}
        </>
      )}
    </div>
  );
};
